/**
 * Mascot — Deej, the djgg pigeon: a coder-gamer pilot pigeon who shows up
 * across the portal (empty states, build progress, the asset studio, admin…).
 * ONE component renders every appearance: pass a context and it picks a random
 * piece from that context's pool in {@link MASCOT_ART} so repeat visits rotate
 * through the set. The art list is GENERATED by tools/mascot-gen from
 * mascot.manifest.json — never hand-edit it; add scenes there and regenerate.
 * Purely decorative: alt text for screen readers, lazy-loaded, no interaction.
 */
import { useMemo } from 'react';
import { MASCOT_ART, type MascotArt, type MascotContext } from './mascotManifest.js';

/** Random member of a context's pool (exported pure for tests). */
export function pickMascotArt(
  context: MascotContext,
  random: () => number = Math.random,
): MascotArt | null {
  const pool = MASCOT_ART.filter((a) => a.context === context);
  if (pool.length === 0) return null;
  return pool[Math.min(pool.length - 1, Math.floor(random() * pool.length))];
}

/**
 * A framed mascot illustration with an optional one-line quip. The pick is
 * stable per mount — pass a `key` to force a re-roll (the wizard keys by step).
 */
export function Mascot({
  context,
  id,
  quip = true,
  size,
}: {
  context: MascotContext;
  id?: string;
  /** Show the art's caption line (when the scene has one). */
  quip?: boolean;
  /** Max width in px (defaults per aspect via CSS). */
  size?: number;
}) {
  const art = useMemo(() => id ? MASCOT_ART.find(art => art.id === id) ?? null : pickMascotArt(context), [context, id]);
  if (!art) return null;
  return (
    <figure
      className={`mascot mascot--${art.aspect.replace(':', 'x')}`}
      style={size ? { maxWidth: size } : undefined}
      data-testid="mascot"
      data-mascot-id={art.id}
    >
      <img className="mascot__img" src={art.src} alt={art.alt} loading="lazy" />
      {quip && art.quip ? <figcaption className="mascot__quip">{art.quip}</figcaption> : null}
    </figure>
  );
}

/**
 * A decorative full-bleed tile for the gallery grid — sized like a GameCard
 * but intentionally NOT a link and NOT a game (own testid; harnesses count
 * game cards by their own selectors).
 */
export function MascotTile() {
  const art = useMemo(() => pickMascotArt('gallery-tile'), []);
  if (!art) return null;
  return (
    <div className="card card--mascot" data-testid="mascot-tile" data-mascot-id={art.id}>
      <img className="card__cover" src={art.src} alt={art.alt} loading="lazy" />
      <div className="card--mascot__label">Deej · resident pigeon, professional beta tester</div>
    </div>
  );
}
